<template>
  <mf-card-container v-model="search" class="mt-5" :title="title" use-search placeholder="Buscar por CPF">
    <template v-slot:filters>
      <mf-tooltip :disabled="hasPiiAccess" max-width="250" top>
        <template #activator="{ on }">
          <span v-on="on">
            <export-data :disabled="!hasPiiAccess" class="mx-2" :data-fetcher="exportCsv" filename="compras_nao_creditadas" ghost />
          </span>
        </template>
        <span>Você precisa ter a permissão de acesso a informações privilegiadas para acessar esse recurso.</span>
      </mf-tooltip>
    </template>
    <paginated-data-table
      :key="'uncreditedPoints-' + skipQuery"
      ref="uncreditedPoints"
      :headers="headers"
      :search="search"
      :query="query"
      :custom-items-per-page="10"
      :query-variables="queryVariables"
      :csv-headers="customCsvHeaders"
      :get-csv-data-shape="getCsvDataShape"
      :queryName="cashbackOrPointsQuery"
      :graphql-uri="cashbackMicroservice"
      :skip="skipQuery"
      csvFilename="compras_nao_creditadas"
      export-all-result
      :export-all-result-incremental-page-size="100"
      @update-options="updateVariables"
    >
      <template v-slot:header.uncredited_cashback="{ header }">
        <div class="d-flex align-center">
          <span style="width: 140px; display: inline-block">{{ header.text }}</span>
          <v-tooltip bottom max-width="192px">
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <mf-icon style="display: table-cell; vertical-align: middle" small color="cinza-0">info</mf-icon>
              </span>
            </template>
            <span> {{ tooltipText }} </span>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:item="{ item }">
        <tr>
          <td :class="{ 'texto-corrido': !hasPiiAccess }">
            <router-link
              v-if="hasPiiAccess"
              class="link link-color text-corrido"
              :to="{ path: `/clientes/detalhes-cliente/${item.document_no}` }"
              target="_blank"
            >
              {{ item.document_no | cpfCnpj }}
            </router-link>
            <v-tooltip v-else max-width="250" top content-class="truncated-tooltip">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  {{ item.document_no }}
                </span>
              </template>
              <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
            </v-tooltip>
          </td>
          <td class="texto-corrido">
            <v-tooltip max-width="250" top content-class="truncated-tooltip" :disabled="hasPiiAccess">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  {{ item.name }}
                </span>
              </template>
              <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
            </v-tooltip>
          </td>
          <td class="texto-corrido">{{ DateTime.fromISO(item.purchase_date).toFormat('dd/MM/yyyy') }}</td>
          <td class="texto-corrido">{{ item.purchases_over_limit }}</td>
          <td class="texto-corrido">{{ item.total_purchases }}</td>
          <td class="texto-corrido">{{ purchasesValue(item) }}</td>
          <td>{{ item.created_at }}</td>
        </tr>
      </template>
    </paginated-data-table>
  </mf-card-container>
</template>

<script>
import { QUERY_GET_PURCHASE_WITHOUT_CASHBACK } from '../../graphql'
import { DateTime } from 'luxon'
import PaginatedDataTable from '@/components/PaginatedDataTable'
import cpfCnpj from '@/filters/cpfCnpj.js'
import localeString from '@/filters/localeString.js'
import ExportData from '@/components/export-data/ExportData.vue'

export default {
  name: 'CashbackPurchasesWithoutPoints',
  components: {
    PaginatedDataTable,
    ExportData,
  },
  data: () => ({
    search: '',
    maxRecords: 0,
    cpfCnpj: cpfCnpj,
    DateTime: DateTime,
    locale: localeString,
    queryVariables: {
      sort: ['purchase_date'],
      sortDirection: ['DESC'],
    },
  }),
  computed: {
    query() {
      return QUERY_GET_PURCHASE_WITHOUT_CASHBACK
    },
    customCsvHeaders() {
      return ['cpf', 'consumidor', 'data da compra', 'quantidade de compras além do limite', 'quantidade total de compras', 'cashback não creditado']
    },
    title() {
      return `Compras que não creditaram cashback`
    },
    cashbackMicroservice() {
      return this.$microservicesUrls['cashback']
    },
    cashbackOrPointsQuery() {
      return 'purchasesWithUncreditedCashback'
    },
    tooltipText() {
      return `Quantidade de cashback que esse consumidor receberia nas compras fora do limite feitas nesse dia.`
    },
    headers() {
      return [
        {
          text: 'CPF',
          value: 'document_no',
          width: '164px',
          sortable: true,
          isDefault: true,
        },
        {
          text: 'Consumidor',
          width: '128px',
          value: 'name',
          sortable: false,
          isDefault: true,
        },
        {
          text: 'Data da compra',
          value: 'purchase_date',
          sortable: true,
          isDefault: true,
        },
        {
          text: 'Quantidade de compras além do limite',
          value: 'purchases_over_limit',
          sortable: true,
          isDefault: true,
        },
        {
          text: 'Quantidade total de compras',
          value: 'total_purchases',
          sortable: true,
          isDefault: true,
        },
        {
          text: 'Cashback não creditado',
          value: 'uncredited_cashback',
          sortable: false,
          isDefault: true,
          width: '200px',
        },
      ]
    },
    skipQuery() {
      return false
    },
    hasPiiAccess() {
      return this.$hasPiiAccess()
    },
  },
  methods: {
    updateVariables(newQueryOptions) {
      const { sortBy, sortDesc } = newQueryOptions

      if (sortBy.length && sortDesc.length) {
        this.queryVariables.sort = sortBy
        this.queryVariables.sortDirection = sortDesc[0] ? ['DESC'] : ['ASC']
      }
    },
    exportCsv() {
      return this.$refs.uncreditedPoints.exportCsv()
    },
    getCsvDataShape(item) {
      return {
        document_no: this.cpfCnpj(item.document_no),
        name: item.name,
        purchase_date: DateTime.fromISO(item.purchase_date).toFormat('dd/MM/yyyy'),
        purchases_over_limit: item.purchases_over_limit,
        total_purchases: item.total_purchases,
        uncredited_cashback: this.purchasesValue(item),
      }
    },
    purchasesValue(item) {
      return `R$${this.locale(item.uncredited_cashback, 2)}`
    },
  },
}
</script>

<style lang="scss">
.link-color {
  color: $marca-0-l40 !important;
}
</style>
